import Image from 'components/Common/Image'
import NumberRating from 'components/Common/NumberRating'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { _searchTileContainerQueryUp } from './_SearchTileBase'
import type { SearchTileLogo } from './SearchTile.types'
import SearchTileRowTitle from './shared/SearchTileRowTitle'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'

const Container = styled.div<{ $density: SearchTileDensity }>`
  grid-area: content-primary;
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${props => props.$density === 'compact' && css`
    display: contents;
  `}
`

const Header = styled.div`
  grid-area: content-header;
`

const Body = styled.div`
  grid-area: content-primary;
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  min-width: 0;
`

const Meta = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  width: 100%;
`

const RatingContainer = styled.div`
  display: block;

  ${_searchTileContainerQueryUp.tablet} {
    display: none;
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    display: block;
  }

  ${_searchTileContainerQueryUp.desktop} {
    display: none;
  }
`

const Logo = styled(Image)`
  object-fit: contain;
  height: ${rem(40)};

  ${_searchTileContainerQueryUp.tablet} {
    display: none;
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    display: block;
  }

  ${_searchTileContainerQueryUp.desktop} {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  cancellationPolicyLabel?: ReactElement | false
  /**
   * Inclusions, amenities, etc.
   */
  features?: ReactElement | false
  labels?: ReactElement | false
  /**
   * Complementary content. _(Sits between header and features)_
   */
  meta?: ReactElement | false
  overline?: ReactElement | false
  rating?: App.OfferRating
  sponsored?: boolean
  title: ReactElement | string
  subtitle?: string
  logo?: SearchTileLogo
  comparisonCheckbox?: ReactElement
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function _SearchTileContentPrimary({
  cancellationPolicyLabel,
  features,
  labels,
  meta,
  overline,
  rating,
  sponsored = false,
  title,
  subtitle,
  logo,
  comparisonCheckbox,
  density = 'default',
}: Props) {
  const headerContent = <Group direction="vertical" gap={4}>
    <Group direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="start">
      <div>
        {comparisonCheckbox}
        <Group direction="vertical" gap={4}>
          {overline}
          <SearchTileRowTitle sponsored={sponsored}>{title}</SearchTileRowTitle>
          {subtitle && <BodyText variant="medium" colour="light-foreground-alt">{subtitle}</BodyText>}
        </Group>
      </div>
      {rating && <RatingContainer>
        <NumberRating
          rating={rating.score}
          total={rating.reviewsTotal}
          variant="small"
          numberPosition="end"
          onlyNumber
        />
      </RatingContainer>}
    </Group>
    {labels}
    {cancellationPolicyLabel}
  </Group>

  const bodyContent = <>
    {logo && <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
      <Meta>{meta}</Meta>
      <Logo image={logo.compact} width={72} dpr={2} />
    </Group>}
    {!logo && meta}
    {features}
  </>

  if (density === 'default') {
    return <Container
      className={_SearchTileContentPrimary.name}
      $density={density}
    >
      {headerContent}
      {bodyContent}
    </Container>
  }

  return <Container
    className={_SearchTileContentPrimary.name}
    $density={density}
  >
    <Header>
      {headerContent}
    </Header>
    <Body>
      {bodyContent}
    </Body>
  </Container>
}

export default _SearchTileContentPrimary
